import { ReactElement, useEffect } from 'react'

import { ErrorMessage, Field, Form, Formik } from 'formik'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { object, string } from 'yup'

import { useAppConfig } from 'appConfig'
import { FormikInput } from 'components/formik'
import { SegmentEventName } from 'const/monitoring/segment'
import { signIn } from 'features/Auth/authService'
import { useAnalytics } from 'features/Monitoring/Segment/useAnalytics'
import { getPath } from 'routing/pagePaths'

import { AuthError, SubmitButton } from '../Shared'
import * as authStyles from '../styles.scss'

const LoginForm = (): ReactElement => {
    const { t } = useTranslation('account')

    const validationSchema = object().shape({
        email: string()
            .email()
            .trim()
            .lowercase()
            .required()
            .label(t('account:email', 'Email')),
        password: string()
            .min(6)
            .required()
            .label(t('account:password', 'Password')),
    })

    const { isChinaBuild, productTitle } = useAppConfig()

    const { trackEvent, trackCtaClick } = useAnalytics()

    useEffect(() => {
        trackEvent(SegmentEventName.LOGIN_STARTED, {
            text: 'Login - Login Started',
        })
    }, [trackEvent])

    const demoLink = isChinaBuild
        ? 'https://www.junglescout.cn/downstream/'
        : 'https://www.junglescout.com/enterprise/demo/'

    return (
        <>
            <h1 className={authStyles.authFormHeading}>
                {t('account:LoginForm.title', 'Log In')}
            </h1>

            <Formik
                initialValues={{
                    email: '',
                    password: '',
                }}
                onSubmit={(values) => {
                    trackCtaClick({
                        destination: 'Signed in - Landing page',
                        text: 'Log In',
                        type: 'button',
                        location: 'Login - Login Page',
                    })
                    signIn(validationSchema.cast(values))
                }}
                validationSchema={validationSchema}
            >
                {() => (
                    <Form>
                        <div style={{ marginBottom: 8 }}>
                            <Field
                                component={FormikInput}
                                placeholder={t('account:email', 'Email')}
                                name="email"
                                size="large"
                                id="email"
                            />
                            <ErrorMessage
                                name="email"
                                component="div"
                                className="fg-control-text is-error"
                            />
                        </div>
                        <div>
                            <Field
                                component={FormikInput}
                                placeholder={t('account:password', 'Password')}
                                name="password"
                                type="password"
                                size="large"
                                id="password"
                            />
                            <ErrorMessage
                                name="password"
                                component="div"
                                className="fg-control-text is-error"
                            />
                        </div>
                        <SubmitButton>
                            {t('account:LoginForm.submitBtnText', 'Log In')}
                        </SubmitButton>
                    </Form>
                )}
            </Formik>

            <div className={authStyles.authFormLinkRow}>
                <Link to={getPath('authForgotPasswordPage')}>
                    <span>
                        {t(
                            'account:LoginForm.forgotPasswordLink',
                            'Forgot password?'
                        )}
                    </span>
                </Link>
            </div>

            <AuthError />

            <footer className={authStyles.authFormFooter}>
                <Trans i18nKey="account:LoginForm.requestDemoLink">
                    Don&apos;t have a {{ productTitle }} account?{' '}
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={demoLink}
                    >
                        <span>Request a Demo</span>
                    </a>
                </Trans>
            </footer>
        </>
    )
}

export default LoginForm
